/**
 * What: Renders the flat Shopify App Bridge navigation used by the embedded app shell.
 * Transport / data-flow: Client-side navigation links built from route helpers that preserve embedded Shopify context.
 *
 */
import { NavMenu, useAppBridge } from '@shopify/app-bridge-react'
import type { MouseEvent } from 'react'
import { isFeatureEnabled, type FeatureEntitlementSnapshot } from '@testfeed/entitlement-contracts'
import { shopifyComponentCopy } from '@testfeed/product-language'
import {
  buildInsightsViewUrl,
  buildHomeViewUrl,
  buildMicroSurveysViewUrl,
  buildPixelEventsViewUrl,
  buildSettingsViewUrl,
  buildSettingsIntegrationsViewUrl,
  buildTestsViewUrl,
  leaveShopifyBuilder,
} from '../resultsNavigation'
import { handleShopifyAppNavigationClick } from './shopifyAppNavigationClick'
import { reportShopifyNavigationFailure } from '../hooks/outside/useShopifyAppNavigation'
import { isAbortError } from '../page-state'
import { getAppBridgeShop } from '../authenticatedShopBootstrap'
import { shopifyUiTracker, shopifyUiFailure, type ShopifyUiActionId } from '../shopifyUiTracking'

type ShopifyAppNavigationProps = {
  entitlements: FeatureEntitlementSnapshot
}

/** Keeps App Bridge navigation links outside the route content component. */
export function ShopifyAppNavigation({ entitlements }: ShopifyAppNavigationProps) {
  const shopify = useAppBridge()
  const showMicroSurveys = isFeatureEnabled(entitlements, 'micro_surveys')
  const showPixelEvents = isFeatureEnabled(entitlements, 'pixel_events')
  const expectedShop = getAppBridgeShop(shopify)
  const handleClick = (event: MouseEvent<HTMLAnchorElement>, action: ShopifyUiActionId) => {
    const tracker = shopifyUiTracker(shopify)
    // Native host history can replay a programmatic link activation; it is not another user intent.
    const attempt = event.isTrusted ? tracker.request(action) : undefined
    void handleShopifyAppNavigationClick(event.nativeEvent, expectedShop, (commit) =>
      leaveShopifyBuilder(shopify, commit),
    )
      .then((accepted) => {
        if (accepted || !event.defaultPrevented) tracker.completed(attempt)
        else tracker.cancelled(attempt, 'user')
      })
      .catch((cause) => {
        if (isAbortError(cause)) {
          tracker.cancelled(attempt, 'aborted')
          return
        }
        tracker.failed(attempt, shopifyUiFailure(cause))
        reportShopifyNavigationFailure(shopify, cause)
      })
  }

  return (
    <NavMenu>
      <a href={buildHomeViewUrl()} rel="home" onClick={(event) => handleClick(event, 'navigate-home')}>
        {shopifyComponentCopy.shopifyAppNavigation.home001}
      </a>
      <a href={buildInsightsViewUrl()} onClick={(event) => handleClick(event, 'navigate-insights')}>
        {shopifyComponentCopy.shopifyAppNavigation.insights008}
      </a>
      <a href={buildTestsViewUrl()} onClick={(event) => handleClick(event, 'navigate-studies')}>
        {shopifyComponentCopy.shopifyAppNavigation.studies002}
      </a>
      {showMicroSurveys && (
        <a href={buildMicroSurveysViewUrl()} onClick={(event) => handleClick(event, 'navigate-micro-surveys')}>
          {shopifyComponentCopy.shopifyAppNavigation.microSurveys003}
        </a>
      )}
      {showPixelEvents && (
        <a href={buildPixelEventsViewUrl()} onClick={(event) => handleClick(event, 'navigate-pixel-events')}>
          {shopifyComponentCopy.shopifyAppNavigation.pixelEvents004}
        </a>
      )}
      <a href={buildSettingsIntegrationsViewUrl()} onClick={(event) => handleClick(event, 'navigate-integrations')}>
        {shopifyComponentCopy.integrationsPage.title}
      </a>
      <a href={buildSettingsViewUrl()} onClick={(event) => handleClick(event, 'navigate-settings')}>
        {shopifyComponentCopy.shopifyAppNavigation.settings007}
      </a>
    </NavMenu>
  )
}
